import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ComplianceDueDiligencePanel = ({ compliance, onChange }) => {
  const agreementStatusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'signed', label: 'Signed' },
    { value: 'expired', label: 'Expired' }
  ];

  const riskLevelOptions = [
    { value: 'low', label: 'Low Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'high', label: 'High Risk' }
  ];

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-success';
      case 'medium': return 'text-warning';
      case 'high': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getAgreementStatusColor = (status) => {
    switch (status) {
      case 'signed': return 'text-success';
      case 'draft': return 'text-warning';
      case 'expired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-24">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground font-heading mb-4">
          Compliance & Due Diligence
        </h3>
        <p className="text-sm text-muted-foreground">
          Track compliance status and risk assessment
        </p>
      </div>
      <div className="bg-card border border-border rounded-lg p-16 md:p-24 space-y-20">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-12">
            <Icon name="FileCheck" size={20} className="text-primary mt-2" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">
                Non-Disclosure Agreement
              </h4>
              <p className="text-xs text-muted-foreground caption">
                NDA status and validity
              </p>
            </div>
          </div>
          <Checkbox
            checked={compliance?.ndaSigned}
            onChange={(e) => onChange('ndaSigned', e?.target?.checked)}
          />
        </div>

        {compliance?.ndaSigned && (
          <div className="ml-32 pl-16 border-l-2 border-success/20">
            <div className="flex items-center gap-8 text-xs text-success caption">
              <Icon name="CheckCircle2" size={14} />
              <span>NDA signed on {new Date(compliance.ndaSignedDate)?.toLocaleDateString()}</span>
            </div>
          </div>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-16 md:p-24 space-y-16">
        <div className="flex items-start gap-12">
          <Icon name="FileText" size={20} className="text-primary" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-foreground mb-4">
              Partner Agreement
            </h4>
            <p className="text-xs text-muted-foreground caption mb-12">
              Current agreement status
            </p>

            <Select
              placeholder="Select agreement status"
              options={agreementStatusOptions}
              value={compliance?.agreementStatus}
              onChange={(value) => onChange('agreementStatus', value)}
            />

            {compliance?.agreementStatus && (
              <div className={`
                flex items-center gap-8 mt-12 text-xs caption
                ${getAgreementStatusColor(compliance?.agreementStatus)}
              `}>
                <Icon name="Info" size={14} />
                <span>
                  {compliance?.agreementStatus === 'signed' && 'Agreement is active and valid'}
                  {compliance?.agreementStatus === 'draft' && 'Agreement pending signature'}
                  {compliance?.agreementStatus === 'expired' && 'Agreement requires renewal'}
                </span>
              </div>
            )}
          </div>
        </div>

        {compliance?.agreementDocument && (
          <div className="ml-32 pl-16 border-l-2 border-border">
            <div className="flex items-center justify-between p-12 bg-muted rounded-lg">
              <div className="flex items-center gap-12 min-w-0 flex-1">
                <Icon name="FileText" size={16} className="text-primary flex-shrink-0" />
                <span className="text-xs text-foreground truncate">
                  {compliance?.agreementDocument}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                iconName="Download"
                iconSize={14}
              />
            </div>
          </div>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-16 md:p-24 space-y-20">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-12">
            <Icon name="Shield" size={20} className="text-primary mt-2" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">
                Sanctions & Compliance Check
              </h4>
              <p className="text-xs text-muted-foreground caption">
                Regulatory compliance verification
              </p>
            </div>
          </div>
          <Checkbox
            checked={compliance?.sanctionsChecked}
            onChange={(e) => onChange('sanctionsChecked', e?.target?.checked)}
          />
        </div>

        {compliance?.sanctionsChecked && (
          <div className="ml-32 pl-16 border-l-2 border-success/20">
            <div className="flex items-center gap-8 text-xs text-success caption">
              <Icon name="CheckCircle2" size={14} />
              <span>Compliance check completed - No issues found</span>
            </div>
          </div>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-16 md:p-24 space-y-16">
        <div className="flex items-start gap-12">
          <Icon name="AlertTriangle" size={20} className="text-warning" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-foreground mb-4">
              Financial Risk Assessment
            </h4>
            <p className="text-xs text-muted-foreground caption mb-12">
              Evaluate financial stability and risk level
            </p>

            <Select
              placeholder="Select risk level"
              options={riskLevelOptions}
              value={compliance?.financialRiskLevel}
              onChange={(value) => onChange('financialRiskLevel', value)}
            />

            {compliance?.financialRiskLevel && (
              <div className={`
                flex items-center gap-8 mt-12 text-xs caption
                ${getRiskColor(compliance?.financialRiskLevel)}
              `}>
                <Icon name="Info" size={14} />
                <span>
                  {compliance?.financialRiskLevel === 'low' && 'Strong financial position, minimal risk'}
                  {compliance?.financialRiskLevel === 'medium' && 'Moderate risk, requires monitoring'}
                  {compliance?.financialRiskLevel === 'high' && 'High risk, additional due diligence required'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {(compliance?.ndaSigned || compliance?.sanctionsChecked || compliance?.agreementStatus || compliance?.financialRiskLevel) && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-16">
          <div className="flex items-start gap-12">
            <Icon name="Sparkles" size={18} className="text-primary mt-2" />
            <div>
              <h4 className="text-sm font-semibold text-primary mb-4">AI Compliance Alert</h4>
              <p className="text-xs text-foreground">
                All compliance checks are on track. Partner agreement expires in 180 days - renewal reminder will be sent 30 days before expiry.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDueDiligencePanel;